// Raster reference tab (Settings → Raster).
//
// Read-only reproduction of the Hessen grading rasters and the operator list,
// rendered from the backend catalog (GET /api/grading/raster → `reference`),
// which is the single source of truth for the criteria. A DE/EN toggle drives
// the language of the *entire* tab: German is the default; switching to English
// translates every heading, label, rubric cell and operator definition. Only
// the tab chrome (titles/descriptions below) is local UI text.

import { useState } from "react";

import { useRasterCatalog } from "../api/hooks";
import type {
  RrBilingual,
  RrOperatorGroup,
  RrSection,
  RrTable,
} from "../api/types";

type Lang = "de" | "en";

// Tab chrome — framing text, not raster domain data.
const RASTER_UI = {
  toggleHint: {
    de: "Referenzraster (Hessen, Stand 1. August 2025) — Standardanzeige auf Deutsch.",
    en: "Reference rubrics (Hessen, as of 1 Aug 2025) — German by default.",
  },
  langDe: "Deutsch",
  langEn: "English",
  section1: { de: "Language", en: "Language" },
  section1Desc: {
    de: "Hinweise zur Bewertung der sprachlichen Leistung in den modernen Fremdsprachen (Anlage 1): Lexik, Grammatik und Textgestaltung über alle Notenspalten.",
    en: "Assessment of linguistic performance in the modern foreign languages (Annex 1): Lexis, Grammar and Text composition across all grade columns.",
  },
  section2: { de: "Content", en: "Content" },
  section2Desc: {
    de: "Hinweise zur Bewertung der inhaltlichen Leistung in den modernen Fremdsprachen (Anlage 2).",
    en: "Assessment of content performance in the modern foreign languages (Annex 2).",
  },
  areaSchreiben: { de: "Kompetenzbereich „Schreiben“", en: "Competence area “Writing”" },
  areaSprachmittlung: { de: "Kompetenzbereich „Sprachmittlung“", en: "Competence area “Mediation”" },
  operators: { de: "Operatoren", en: "Operators" },
  operatorsTitle: {
    de: "Operatoren im Fach Englisch und den bilingualen Sachfächern des FB II mit der Zielsprache Englisch",
    en: "Operators for English and the bilingual subjects of FB II with English as the target language",
  },
  operatorsIntro: {
    de: "In der folgenden Tabelle sind die für die oben genannten Fächer relevanten Operatoren definiert und mit fachspezifischen Beispielen unterlegt. Die Operatoren sind dem Anforderungsbereich zugeordnet, in welchem sie jeweils ihren Schwerpunkt haben. Die konkrete Zuordnung eines Operators zu den Anforderungsbereichen ist vom Kontext der Aufgabenstellung abhängig. Anmerkung: Je nach Material wird die Schreibweise der Operatoren in der Aufgabenstellung ggf. der jeweiligen Varietät angepasst (z. B. summarize – summarise).",
    en: "The table below defines the operators relevant to the subjects named above and illustrates them with subject-specific examples. Each operator is assigned to the requirement area in which it has its main emphasis; the actual assignment depends on the context of the task. Note: depending on the material, the spelling of operators in the task may be adapted to the respective variety (e.g. summarize – summarise).",
  },
  opOperator: { de: "Operator(en)", en: "Operator(s)" },
  opDefinition: { de: "Definition", en: "Definition" },
  opExamples: { de: "Beispiel(e)", en: "Example(s)" },
  opAfb: { de: "AFB", en: "Requirement area" },
} as const;

/** Render a cell whose text may contain "\n"-separated aspect lines. */
function CellText({ value, lang }: { value: RrBilingual; lang: Lang }) {
  return (
    <>
      {value[lang].split("\n").map((line, i) => (
        <span key={i} className="rr-line">
          {line}
        </span>
      ))}
    </>
  );
}

function RubricTable({
  table,
  lang,
  unit,
  grades,
}: {
  table: RrTable;
  lang: Lang;
  unit: RrBilingual;
  grades: { de: string; en: string; range: string }[];
}) {
  return (
    <div className="rr-table-block">
      <div className="rr-table-wrap">
        {table.title && <div className="rr-subtask">{table.title[lang]}</div>}
        <table className="rr-table">
          <thead>
            <tr>
              <th className="rr-crit-col" />
              {grades.map((g) => (
                <th key={g.de}>
                  <span className="rr-grade">{g[lang]}</span>
                  <span className="rr-points">
                    {g.range} {unit[lang]}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.sections.map((section, si) => (
              <RubricRows key={si} section={section} lang={lang} cols={grades.length} />
            ))}
          </tbody>
        </table>
      </div>
      {table.note && <div className="rr-note">{table.note[lang]}</div>}
    </div>
  );
}

function RubricRows({ section, lang, cols }: { section: RrSection; lang: Lang; cols: number }) {
  return (
    <>
      {section.heading && (
        <tr className="rr-group">
          <td colSpan={cols + 1}>{section.heading[lang]}</td>
        </tr>
      )}
      {section.rows.map((row, ri) => (
        <tr key={ri}>
          <td className="rr-crit">{row.label[lang]}</td>
          {row.cells.map((cell, ci) => (
            <td key={ci}>
              <CellText value={cell} lang={lang} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function OperatorRows({ group, lang }: { group: RrOperatorGroup; lang: Lang }) {
  return (
    <>
      <tr className="rr-group">
        <td colSpan={4}>{group.heading[lang]}</td>
      </tr>
      {group.rows.map((row, ri) => (
        <tr key={ri}>
          <td className="rr-op-name">
            <span className="rr-op-term">{row.term}</span>
            {lang === "de" && <span className="rr-op-gloss">{row.gloss}</span>}
          </td>
          <td>{row.definition[lang]}</td>
          <td>
            {row.examples.map((ex, i) => (
              <span key={i} className="rr-line">
                {ex}
              </span>
            ))}
          </td>
          <td className="rr-afb">{row.afb}</td>
        </tr>
      ))}
    </>
  );
}

export function RasterReference() {
  const [lang, setLang] = useState<Lang>("de");
  const { data: catalog } = useRasterCatalog();
  const ref = catalog?.reference;

  if (!ref) return <div className="rr" />;

  return (
    <div className="rr">
      <div className="rr-toolbar">
        <span className="rr-toolbar-hint">{RASTER_UI.toggleHint[lang]}</span>
        <div className="rr-toggle" role="group" aria-label={RASTER_UI.toggleHint[lang]}>
          <button
            type="button"
            className={`rr-toggle-btn${lang === "de" ? " active" : ""}`}
            onClick={() => setLang("de")}
            aria-pressed={lang === "de"}
          >
            {RASTER_UI.langDe}
          </button>
          <button
            type="button"
            className={`rr-toggle-btn${lang === "en" ? " active" : ""}`}
            onClick={() => setLang("en")}
            aria-pressed={lang === "en"}
          >
            {RASTER_UI.langEn}
          </button>
        </div>
      </div>

      {/* Section 1 — Language raster */}
      <section className="rr-section">
        <h3 className="rr-section-title">{RASTER_UI.section1[lang]}</h3>
        <p className="rr-section-desc">{RASTER_UI.section1Desc[lang]}</p>
        <RubricTable
          table={{ sections: ref.language_table.sections }}
          lang={lang}
          unit={ref.unit_punkte}
          grades={ref.grades}
        />
        <div className="rr-notes">
          <div className="rr-notes-title">{ref.language_notes.title[lang]}</div>
          <ul>
            {ref.language_notes.items.map((item, i) => (
              <li key={i}>{item[lang]}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Section 2 — Content raster */}
      <section className="rr-section">
        <h3 className="rr-section-title">{RASTER_UI.section2[lang]}</h3>
        <p className="rr-section-desc">{RASTER_UI.section2Desc[lang]}</p>

        <h4 className="rr-area-title">{RASTER_UI.areaSchreiben[lang]}</h4>
        {ref.schreiben_tables.map((table, i) => (
          <RubricTable key={i} table={table} lang={lang} unit={ref.unit_notenpunkte} grades={ref.grades} />
        ))}

        <h4 className="rr-area-title">{RASTER_UI.areaSprachmittlung[lang]}</h4>
        <RubricTable table={ref.sprachmittlung_table} lang={lang} unit={ref.unit_notenpunkte} grades={ref.grades} />
      </section>

      {/* Operators */}
      <section className="rr-section">
        <h3 className="rr-section-title">{RASTER_UI.operators[lang]}</h3>
        <p className="rr-section-desc">{RASTER_UI.operatorsTitle[lang]}</p>
        <p className="rr-op-intro">{RASTER_UI.operatorsIntro[lang]}</p>
        <div className="rr-table-wrap">
          <table className="rr-table rr-op-table">
            <thead>
              <tr>
                <th>{RASTER_UI.opOperator[lang]}</th>
                <th>{RASTER_UI.opDefinition[lang]}</th>
                <th>{RASTER_UI.opExamples[lang]}</th>
                <th className="rr-afb-col">{RASTER_UI.opAfb[lang]}</th>
              </tr>
            </thead>
            <tbody>
              {ref.operators.map((group) => (
                <OperatorRows key={group.heading.de} group={group} lang={lang} />
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
